import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import QRCode from 'qrcode';
import QRCodeSVG from 'react-qr-code';
import { jsPDF } from 'jspdf';
import { FaSearch, FaDownload, FaEye, FaTimes, FaBox, FaInfo, FaImage, FaTh, FaTable, FaMoon, FaSun, FaQrcode, FaFilePdf } from 'react-icons/fa';

const TilesStockStatus = () => {
  const { user } = useContext(AuthContext);
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(16);
  const [viewMode, setViewMode] = useState('cards');
  const [selectedTile, setSelectedTile] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });

  // Enhanced pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
    itemsPerPage: 16,
    startIndex: 0,
    endIndex: 0,
    pageRange: { start: 1, end: 1 }
  });

  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalTiles: 0,
    currentPageItems: 0,
    stockSummary: {
      outOfStock: 0,
      lowStock: 0,
      available: 0
    }
  });

  // Filter states
  const [filters, setFilters] = useState({
    brand: '',
    category: '',
    status: 'active',
    stockFilter: '', // 'low', 'out', 'available'
    sortBy: 'tilesName',
    sortOrder: 'asc'
  });
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  // Stock editing states
  const [showStockEditModal, setShowStockEditModal] = useState(false);
  const [selectedStockEdit, setSelectedStockEdit] = useState(null);
  const [stockEditLoading, setStockEditLoading] = useState(false);
  const [editingStock, setEditingStock] = useState({
    freshStockBox: 0,
    freshStockPieces: 0,
    damagedStockBox: 0,
    damagedStockPieces: 0
  });

  // Image editing states
  const [showImageEditModal, setShowImageEditModal] = useState(false);
  const [selectedImageEdit, setSelectedImageEdit] = useState(null);
  const [imageEditLoading, setImageEditLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Status change states
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // QR Code states
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrls, setQrCodeUrls] = useState({ objectId: '', partNumber: '' });
  const [selectedTileForQR, setSelectedTileForQR] = useState(null);
  const [qrName, setQrName] = useState(null);
  const [activeQRType, setActiveQRType] = useState('objectId'); // 'objectId' or 'partNumber'

  // Dark mode toggle function
  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  // Sync dark mode across components
  useEffect(() => {
    const syncDarkMode = () => {
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode) {
        setIsDarkMode(JSON.parse(savedMode));
      }
    };

    // Poll for changes every 500ms
    const interval = setInterval(syncDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  // Add status change function
  const handleStatusChange = async (tile) => {
    try {
      setStatusChangeLoading(true);
      const newStatus = tile.status === 'active' ? 'inactive' : 'active';
      
      const confirmMessage = `Are you sure you want to ${newStatus === 'inactive' ? 'deactivate' : 'activate'} this tile?`;
      
      const confirmed = await Swal.fire({
        title: confirmMessage,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      });

      if (!confirmed.isConfirmed) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/update-status/${tile._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const result = await response.json();

      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: result.message,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        fetchTiles(); // Refresh the tiles list
      } else {
        Swal.fire({
          title: 'Error!',
          text: result.message || 'Failed to update tile status',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating tile status:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update tile status. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Fetch tiles data with server-side pagination
  const fetchTiles = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters for API call
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: searchQuery,
        userId: user.user._id,
        ...filters
      });

      console.log('Fetching tiles with params:', queryParams.toString());

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tiles/stock-status?${queryParams}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setTiles(result.data.tiles || []);
        setPagination(result.data.pagination || {});
        setSummaryStats(result.data.summary || {});
      } else {
        throw new Error(result.message || 'Failed to fetch tiles');
      }
    } catch (error) {
      console.error('Error fetching tiles:', error);
      setError(error.message);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch tiles data. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandsAndCategories = async () => {
    try {
      const [brandsResponse, categoriesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/brands?userId=${user.user._id}`),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/parameters/all?userId=${user.user._id}`)
      ]);

      if (brandsResponse.ok) {
        const brandsData = await brandsResponse.json();
        setBrands(Array.isArray(brandsData) ? brandsData : brandsData.brands || []);
      }
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        const categoryParams = Array.isArray(categoriesData) 
          ? categoriesData.filter(p => p.assignedFor === 'category') 
          : categoriesData?.filter?.(p => p.assignedFor === 'category') || [];
        setCategories(categoryParams);
      }
    } catch (error) {
      console.error('Error fetching brands and categories:', error);
    }
  };

  // Stock editing functions
  const handleEditStock = async (tile, stockEntry) => {
    try {
      // Check if tile is used in transactions
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tiles/check-tile-usage/${tile._id}/${stockEntry.location._id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      const result = await response.json();
      
      if (!result.canEdit) {
        Swal.fire({
          title: 'Cannot Edit Stock',
          text: 'This tile has been used in transactions and cannot be edited.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      setSelectedStockEdit({ tile, stockEntry });
      setEditingStock({
        freshStockBox: stockEntry.freshStockBox || 0,
        freshStockPieces: stockEntry.freshStockPieces || 0,
        damagedStockBox: stockEntry.damagedStockBox || 0,
        damagedStockPieces: stockEntry.damagedStockPieces || 0
      });
      setShowStockEditModal(true);
    } catch (error) {
      console.error('Error checking tile usage:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error checking if tile can be edited.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleSaveStock = async () => {
    if (!selectedStockEdit) return;

    setStockEditLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tiles/edit-stock/${selectedStockEdit.tile._id}/${selectedStockEdit.stockEntry.location._id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(editingStock)
        }
      );

      const result = await response.json();
      
      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Stock updated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        setShowStockEditModal(false);
        fetchTiles(); // Refresh data
      } else {
        Swal.fire({
          title: 'Error!',
          text: result.message || 'Failed to update stock.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error updating stock.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setStockEditLoading(false);
    }
  };

  // Image editing functions
  const handleEditImage = (tile) => {
    setSelectedImageEdit(tile);
    setSelectedImage(null);
    setShowImageEditModal(true);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: 'File too large',
          text: 'Image size should be less than 5MB.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: 'Invalid file type',
          text: 'Only JPG, JPEG and PNG images are allowed.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      
      setSelectedImage(file);
    }
  };

  const handleSaveImage = async () => {
    if (!selectedImageEdit || !selectedImage) return;

    setImageEditLoading(true);
    try {
      const formData = new FormData();
      formData.append('tilesImage', selectedImage);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tiles/update-image/${selectedImageEdit._id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        }
      );

      const result = await response.json();
      
      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Image updated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        setShowImageEditModal(false);
        fetchTiles(); // Refresh data
      } else {
        Swal.fire({
          title: 'Error!',
          text: result.message || 'Failed to update image.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error updating image:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error updating image.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setImageEditLoading(false);
    }
  };

  // Event handlers
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(1); // Reset to first page when filtering
  };

  const resetFilters = () => {
    setFilters({
      brand: '',
      category: '',
      status: 'active',
      stockFilter: '',
      sortBy: 'tilesName',
      sortOrder: 'asc'
    });
    setSearchQuery('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGenerateQR = async (tile) => {
    try {
      const qrCodes = {};
      
      // Generate QR code for ObjectId (always)
      const objectIdQR = await QRCode.toDataURL(tile._id, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      qrCodes.objectId = objectIdQR;
      
      // Generate QR code for Part Number (only if it exists)
      if (tile.partNumber && tile.partNumber.trim() !== '') {
        const partNumberQR = await QRCode.toDataURL(tile.partNumber, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        qrCodes.partNumber = partNumberQR;
      }
      
      setQrCodeUrls(qrCodes);
      setSelectedTileForQR(tile);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      Swal.fire('Error', 'Failed to generate QR code', 'error');
    }
  };

  const handleDownloadQR = (type) => {
    const link = document.createElement('a');
    const qrUrl = type === 'objectId' ? qrCodeUrls.objectId : qrCodeUrls.partNumber;
    const fileName = type === 'objectId' 
      ? `tile-${selectedTileForQR?.id || selectedTileForQR?._id}-objectid-qr.png`
      : `tile-${selectedTileForQR?.id || selectedTileForQR?._id}-partno-qr.png`;
    
    link.download = fileName;
    link.href = qrUrl;
    link.click();
  };

  // PDF download functions (matching TallyItemsManagement format)
  const downloadQRPdf = async (includeQrName = false) => {
    const svgId = activeQRType === 'partNumber' ? 'qr-code-svg-partno' : 'qr-code-svg-objid';
    const svg = document.getElementById(svgId);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngDataUrl = canvas.toDataURL('image/png');
      
      if (includeQrName) {
        downloadQRWithNamePdf(pngDataUrl);
      } else {
        downloadQROnlyPdf(pngDataUrl);
      }
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const downloadQROnlyPdf = (qrImageData) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65]
    });
    pdf.addImage(qrImageData, 'PNG', 5, 15, 35, 35);
    const tileName = selectedTileForQR?.tilesName || 'Tile';
    const suffix = activeQRType === 'partNumber' ? '_PartNo_QR' : '_QR';
    pdf.save(`${tileName}${suffix}.pdf`);
  };

  const downloadQRWithNamePdf = (qrImageData) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65]
    });
    
    const qrNameText = qrName || 'DOMUS';
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    const qrNameWidth = pdf.getTextWidth(qrNameText);
    const qrNameX = (45 - qrNameWidth) / 2;
    const qrNameY = 8;
    pdf.text(qrNameText, qrNameX, qrNameY);
    
    const qrSize = 25;
    const qrX = (45 - qrSize) / 2;
    const qrY = qrNameY + 4;
    pdf.addImage(qrImageData, 'PNG', qrX, qrY, qrSize, qrSize);
    
    const arrowY = qrY + qrSize + 4;
    const arrowX = 45 / 2;
    pdf.setLineWidth(0.5);
    pdf.line(arrowX, arrowY, arrowX, arrowY + 3);
    pdf.line(arrowX - 1, arrowY + 2, arrowX, arrowY + 3);
    pdf.line(arrowX + 1, arrowY + 2, arrowX, arrowY + 3);
    
    const itemNameText = selectedTileForQR?.tilesName || 'Tile';
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    const maxWidth = 38;
    const lines = pdf.splitTextToSize(itemNameText, maxWidth);
    const lineHeight = 3.0;
    const startY = arrowY + 8;
    
    lines.forEach((line, index) => {
      const lineWidth = pdf.getTextWidth(line);
      const lineX = (45 - lineWidth) / 2;
      const lineY = startY + (index * lineHeight);
      pdf.text(line, lineX, lineY);
    });
    
    const suffix = activeQRType === 'partNumber' ? '_PartNo_QR_with_name' : '_QR_with_name';
    pdf.save(`${selectedTileForQR?.tilesName || 'Tile'}${suffix}.pdf`);
  };

  const openDetailsModal = (tile) => {
    setSelectedTile(tile);
    setShowDetailsModal(true);
  };

  // Effects
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTiles();
    }, 300); // Debounce search and filters
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, page, limit, filters]);

  // Initialize data fetching
  useEffect(() => {
    fetchBrandsAndCategories();
  }, []);

  // Fetch company QR name
  useEffect(() => {
    const fetchCompanyQrName = async () => {
      try {
        const companyId = user?.user?.company?._id || user?.user?.company;
        if (companyId) {
          const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/companies/${companyId}`);
          setQrName(res.data.qrName);
        }
      } catch (error) {
        console.error('Error fetching company QR name:', error);
      }
    };
    fetchCompanyQrName();
  }, []);

  // Calculate stock status for a tile
  const getStockStatus = (tile) => {
    const totalStock = tile.totalFreshBoxes || 0;
    if (totalStock === 0) return { status: 'out', color: 'red', label: 'Out of Stock' };
    if (totalStock < 10) return { status: 'low', color: 'yellow', label: 'Low Stock' };
    return { status: 'available', color: 'green', label: 'Available' };
  };

  // Enhanced pagination component
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const getPageNumbers = () => {
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (let i = Math.max(2, page - delta); i <= Math.min(pagination.totalPages - 1, page + delta); i++) {
        range.push(i);
      }

      if (page - delta > 2) {
        rangeWithDots.push(1, '...');
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (page + delta < pagination.totalPages - 1) {
        rangeWithDots.push('...', pagination.totalPages);
      } else if (pagination.totalPages > 1) {
        rangeWithDots.push(pagination.totalPages);
      }

      return rangeWithDots;
    };

    return (
      <div className={`rounded-lg shadow-sm border p-4 sm:p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Results info */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
            {/* Desktop results display */}
            <p className={`text-xs sm:text-sm hidden sm:block ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Showing <span className="font-medium">{pagination.startIndex || 1}</span> to{' '}
              <span className="font-medium">{pagination.endIndex || pagination.itemsPerPage}</span> of{' '}
              <span className="font-medium">{pagination.totalItems}</span> results
            </p>
            
            {/* Mobile results display */}
            <p className={`text-xs sm:hidden ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-medium">{pagination.startIndex || 1}-{pagination.endIndex || pagination.itemsPerPage}</span>
              {' '}of{' '}
              <span className="font-medium">{pagination.totalItems}</span>
            </p>
            
            {/* Items per page selector */}
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setPage(1);
              }}
              className={`px-2 sm:px-3 py-1 border rounded text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-200' 
                  : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              <option value={8} className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>
                <span className="hidden sm:inline">8 per page</span>
                <span className="sm:hidden">8</span>
              </option>
              <option value={16} className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>
                <span className="hidden sm:inline">16 per page</span>
                <span className="sm:hidden">16</span>
              </option>
              <option value={24} className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>
                <span className="hidden sm:inline">24 per page</span>
                <span className="sm:hidden">24</span>
              </option>
              <option value={48} className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>
                <span className="hidden sm:inline">48 per page</span>
                <span className="sm:hidden">48</span>
              </option>
            </select>
          </div>
          
          {/* Pagination controls */}
          <nav className="flex items-center justify-center space-x-1">
            {/* First page */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
              className={`hidden sm:inline-flex relative items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? 'text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
              title="First page"
            >
              ««
            </button>
            
            {/* Previous page */}
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={!pagination.hasPrevPage}
              className={`relative inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? 'text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            {/* Desktop: Page numbers */}
            <div className="hidden sm:flex items-center space-x-1">
              {getPageNumbers().map((pageNum, index) => (
                pageNum === '...' ? (
                  <span key={index} className={`px-2 sm:px-3 py-2 text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>...</span>
                ) : (
                  <button
                    key={index}
                    onClick={() => handlePageChange(pageNum)}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-md transition-colors ${
                      pageNum === page
                        ? isDarkMode
                          ? 'z-10 bg-blue-600 border-blue-500 text-white'
                          : 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              ))}
            </div>

            {/* Mobile: Compact page display */}
            <div className="sm:hidden flex items-center">
              <span className={`px-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {page} / {pagination.totalPages}
              </span>
            </div>
            
            {/* Next page */}
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={!pagination.hasNextPage}
              className={`relative inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? 'text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
            
            {/* Last page */}
            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={page === pagination.totalPages}
              className={`hidden sm:inline-flex relative items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? 'text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
              title="Last page"
            >
              »»
            </button>
          </nav>
        </div>
        
        {/* Quick page jump */}
        <div className={`mt-4 flex items-center justify-center gap-2 text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <span>Jump to page:</span>
          <input
            type="number"
            min={1}
            max={pagination.totalPages}
            value={page}
            onChange={(e) => {
              const newPage = parseInt(e.target.value);
              if (newPage >= 1 && newPage <= pagination.totalPages) {
                handlePageChange(newPage);
              }
            }}
            className={`w-12 sm:w-16 px-2 py-1 border rounded text-center focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-gray-200'
                : 'border-gray-300 bg-white text-gray-700'
            }`}
          />
          <span>of {pagination.totalPages}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen py-4 sm:py-6 transition-colors duration-200 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Modern Gradient Header */}
        <div className={`rounded-xl shadow-lg mb-6 overflow-hidden ${
          isDarkMode 
            ? 'bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800' 
            : 'bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-700'
        }`}>
          <div className="px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className={`p-3 sm:p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white bg-opacity-20'}`}>
                  <FaBox className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-1">
                    Tiles Stock Status
                  </h1>
                  <p className="text-xs sm:text-sm text-white text-opacity-90">
                    Monitor and manage tiles inventory across all locations
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Stock Summary Stats - Desktop */}
                <div className="hidden lg:flex gap-2">
                  <div className="bg-green-500 bg-opacity-90 text-white px-4 py-2 rounded-lg text-sm font-medium backdrop-blur-sm shadow-sm">
                    <span className="font-bold">{summaryStats.stockSummary.available}</span> Available
                  </div>
                  <div className="bg-yellow-500 bg-opacity-90 text-white px-4 py-2 rounded-lg text-sm font-medium backdrop-blur-sm shadow-sm">
                    <span className="font-bold">{summaryStats.stockSummary.lowStock}</span> Low Stock
                  </div>
                  <div className="bg-red-500 bg-opacity-90 text-white px-4 py-2 rounded-lg text-sm font-medium backdrop-blur-sm shadow-sm">
                    <span className="font-bold">{summaryStats.stockSummary.outOfStock}</span> Out of Stock
                  </div>
                </div>

              </div>
            </div>

            {/* Mobile Stock Summary */}
            <div className="lg:hidden flex gap-2 mt-4">
              <div className="flex-1 bg-green-500 bg-opacity-90 text-white px-3 py-2 rounded-lg text-xs font-medium backdrop-blur-sm text-center shadow-sm">
                <span className="font-bold">{summaryStats.stockSummary.available}</span> Available
              </div>
              <div className="flex-1 bg-yellow-500 bg-opacity-90 text-white px-3 py-2 rounded-lg text-xs font-medium backdrop-blur-sm text-center shadow-sm">
                <span className="font-bold">{summaryStats.stockSummary.lowStock}</span> Low
              </div>
              <div className="flex-1 bg-red-500 bg-opacity-90 text-white px-3 py-2 rounded-lg text-xs font-medium backdrop-blur-sm text-center shadow-sm">
                <span className="font-bold">{summaryStats.stockSummary.outOfStock}</span> Out
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Filters Section */}
        <div className={`rounded-xl shadow-lg border mb-6 overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gradient-to-r from-gray-700 to-gray-800' : 'border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
            <h2 className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              🔍 Search & Filters
            </h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              {/* Enhanced Search Bar */}
              <div className="lg:col-span-2">
                <label className={`block text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Search Tiles
                </label>
                <div className="relative group">
                  <FaSearch className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${
                    isDarkMode ? 'text-gray-400 group-focus-within:text-blue-400' : 'text-gray-400 group-focus-within:text-blue-600'
                  }`} />
                  <input
                    type="text"
                    placeholder="Search by name, brand, category, size..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className={`w-full pl-11 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:bg-gray-600' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-blue-50'
                    }`}
                  />
                </div>
              </div>

              {/* Brand Filter */}
              <div>
                <label className={`block text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Brand
                </label>
                <select
                  value={filters.brand}
                  onChange={(e) => handleFilterChange('brand', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>All Brands</option>
                  {brands.map(brand => (
                    <option key={brand._id} value={brand._id} className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>{brand.name}</option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className={`block text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Category
                </label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>All Categories</option>
                  {categories.map(category => (
                    <option key={category._id} value={category._id} className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>{category.parameterName}</option>
                  ))}
                </select>
              </div>

              {/* Stock Filter */}
              <div>
                <label className={`block text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Stock Status
                </label>
                <select
                  value={filters.stockFilter}
                  onChange={(e) => handleFilterChange('stockFilter', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>All Stock</option>
                  <option value="available" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>Available</option>
                  <option value="low" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>Low Stock</option>
                  <option value="out" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>Out of Stock</option>
                </select>
              </div>

              {/* Reset Button */}
              <div>
                <label className="block text-xs font-semibold mb-2 opacity-0">Reset</label>
                <button
                  onClick={resetFilters}
                  className={`w-full px-4 py-3 border-2 rounded-lg transition-all text-sm font-medium ${
                    isDarkMode 
                      ? 'border-gray-600 text-gray-200 hover:bg-gray-700 hover:border-gray-500' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
                  }`}
                >
                  🔄 Reset Filters
                </button>
              </div>
            </div>

            {/* View & Sort Controls */}
            <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between mt-6 pt-6 border-t gap-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>View Mode:</span>
                <div className={`flex rounded-lg border-2 overflow-hidden shadow-sm ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`px-4 py-2 text-sm font-medium transition-all flex items-center gap-2 ${
                      viewMode === 'cards' 
                        ? 'bg-blue-600 text-white shadow-inner' 
                        : isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaTh className="w-4 h-4" />
                    <span className="hidden sm:inline">Cards</span>
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-4 py-2 text-sm font-medium transition-all flex items-center gap-2 ${
                      viewMode === 'table' 
                        ? 'bg-blue-600 text-white shadow-inner' 
                        : isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaTable className="w-4 h-4" />
                    <span className="hidden sm:inline">Table</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sort by:</span>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className={`px-4 py-2 border-2 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="tilesName" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>Name</option>
                  <option value="brand" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>Brand</option>
                  <option value="createdAt" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>Date Added</option>
                  <option value="totalFreshBoxes" className={isDarkMode ? 'bg-gray-700' : 'bg-white'}>Stock Level</option>
                </select>
                <button
                  onClick={() => handleFilterChange('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')}
                  className={`px-3 py-2 border-2 rounded-lg text-sm font-bold transition-all ${
                    isDarkMode 
                      ? 'border-gray-600 text-gray-200 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                  title={filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {filters.sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className={`rounded-lg shadow-sm border p-12 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading tiles data...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={`rounded-lg shadow-sm border p-6 ${isDarkMode ? 'bg-gray-800 border-red-900' : 'bg-white border-red-200'}`}>
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-red-900' : 'bg-red-100'}`}>
                <FaTimes className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Error Loading Data</h3>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{error}</p>
              <button
                onClick={fetchTiles}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Tiles Grid/Table */}
        {!loading && !error && (
          <>
            {viewMode === 'cards' ? (
              /* Cards View */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-6">
                {tiles.map((tile) => {
                  const stockStatus = getStockStatus(tile);
                  return (
                    <div key={tile._id} className={`rounded-xl shadow-lg border-2 overflow-hidden hover:shadow-2xl hover:scale-105 transition-all duration-300 ${
                      isDarkMode ? 'bg-gray-800 border-gray-700 hover:border-blue-500' : 'bg-white border-gray-200 hover:border-blue-400'
                    }`}>
                      <div className="relative aspect-w-16 aspect-h-9 bg-gradient-to-br from-gray-200 to-gray-300">
                        {tile.imageIds && tile.imageIds.length > 0 ? (
                          <>
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL}/tiles/image/${tile._id}`}
                              alt={tile.tilesName}
                              className="w-full h-48 object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className={`w-full h-48 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`} style={{ display: 'none' }}>
                              <FaImage className="w-10 h-10 mb-2" />
                              <span className="text-sm font-medium">No image</span>
                            </div>
                          </>
                        ) : (
                          <div className={`w-full h-48 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            <FaImage className="w-10 h-10 mb-2" />
                            <span className="text-sm font-medium">No image</span>
                          </div>
                        )}
                        <div className="absolute top-3 right-3">
                          <span className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-lg backdrop-blur-sm ${
                            stockStatus.status === 'available' ? 'bg-green-500 text-white' :
                            stockStatus.status === 'low' ? 'bg-yellow-500 text-white' :
                            'bg-red-500 text-white'
                          }`}>
                            {stockStatus.label}
                          </span>
                        </div>
                      </div>

                      <div className="p-5">
                        <h3 className={`font-bold text-base mb-3 line-clamp-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          {tile.tilesName}
                        </h3>

                        <div className={`space-y-2 text-xs mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">ID:</span>
                            <span className={`font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tile.id}</span>
                          </div>
                          {tile.partNumber && (
                            <div className="flex items-center justify-between">
                              <span className="font-medium">Part#:</span>
                              <span className={`font-semibold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{tile.partNumber}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Brand:</span>
                            <span className="font-semibold">{tile.brand?.name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Category:</span>
                            <span className="font-semibold truncate ml-2">{tile.tilesCategory?.parameterName || 'N/A'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Size:</span>
                            <span className="font-semibold">{tile.tilesMM?.parameterName || 'N/A'} MM</span>
                          </div>
                        </div>

                        <div className={`rounded-lg p-3 mb-3 ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-gray-600' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
                          <div className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            📦 Total Stock
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                {tile.totalFreshBoxes || 0}
                              </span>
                              <span className={`text-xs ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Boxes</span>
                            </div>
                            <div className={`text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              <div className="text-sm font-semibold">{tile.totalFreshPieces || 0} Pieces</div>
                            </div>
                          </div>
                          {tile.inTransitBoxes > 0 && (
                            <div className={`flex items-center justify-between mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-blue-200'}`}>
                              <span className={`text-xs font-semibold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                🚚 In-Transit
                              </span>
                              <span className={`text-xs font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                {tile.inTransitBoxes} Boxes
                              </span>
                            </div>
                          )}
                          {tile.boxesOrdered > 0 && (
                            <div className={`flex items-center justify-between mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-blue-200'}`}>
                              <span className={`text-xs font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                📋 Ordered
                              </span>
                              <span className={`text-xs font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                {tile.boxesOrdered} Boxes
                              </span>
                            </div>
                          )}
                          {(tile.salesOrder?.freshBoxes > 0 || tile.salesOrder?.freshPieces > 0 || tile.salesOrder?.cornerDamagedBoxes > 0) && (
                            <div className={`flex items-center justify-between mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-blue-200'}`}>
                              <span className={`text-xs font-semibold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                🛒 Sales Ordered
                              </span>
                              <span className={`text-xs font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                {parseFloat(((tile.salesOrder?.freshBoxes || 0) + (tile.salesOrder?.cornerDamagedBoxes || 0)).toFixed(4))} Boxes{tile.salesOrder?.freshPieces > 0 ? `, ${parseFloat(tile.salesOrder.freshPieces.toFixed(4))} Pcs` : ''}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => openDetailsModal(tile)}
                            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                          >
                            <FaEye className="w-4 h-4" />
                            <span>Details</span>
                          </button>
                          <button
                            onClick={() => handleGenerateQR(tile)}
                            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all shadow-md hover:shadow-lg"
                            title="Generate QR Code"
                          >
                            <FaQrcode className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditImage(tile)}
                            className={`text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all shadow-md hover:shadow-lg ${
                              isDarkMode 
                                ? 'bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500' 
                                : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
                            }`}
                          >
                            <FaImage className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Enhanced Table View */
              <div className={`rounded-xl shadow-lg border-2 overflow-hidden mb-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className={`${isDarkMode ? 'bg-gradient-to-r from-gray-700 to-gray-800' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
                      <tr>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Tile</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Details</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Stock Status</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Total Stock</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Status</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                      {tiles.map((tile) => {
                        const stockStatus = getStockStatus(tile);
                        return (
                          <tr key={tile._id} className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-blue-50'} transition-colors duration-150`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-14 w-14 flex-shrink-0 relative">
                                  {tile.imageIds && tile.imageIds.length > 0 ? (
                                    <>
                                      <img
                                        src={`${process.env.REACT_APP_API_BASE_URL}/tiles/image/${tile._id}`}
                                        alt={tile.tilesName}
                                        className="h-14 w-14 rounded-xl object-cover shadow-md"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                      <div className={`h-14 w-14 rounded-xl flex flex-col items-center justify-center shadow-md ${
                                        isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400'
                                      }`} style={{ display: 'none' }}>
                                        <FaImage className="w-4 h-4" />
                                      </div>
                                    </>
                                  ) : (
                                    <div className={`h-14 w-14 rounded-xl flex flex-col items-center justify-center shadow-md ${
                                      isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400'
                                    }`}>
                                      <FaImage className="w-4 h-4" />
                                    </div>
                                  )}
                                </div>
                                <div className="ml-4">
                                  <div className={`text-sm font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{tile.tilesName}</div>
                                  <div className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>ID: {tile.id}</div>
                                  {tile.partNumber && (
                                    <div className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Part#: {tile.partNumber}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              <div className="space-y-1">
                                <div><span className="font-medium">Brand:</span> {tile.brand?.name || 'N/A'}</div>
                                <div><span className="font-medium">Category:</span> {tile.tilesCategory?.parameterName || 'N/A'}</div>
                                <div><span className="font-medium">Size:</span> {tile.tilesMM?.parameterName || 'N/A'} MM</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${
                                stockStatus.status === 'available' ? 'bg-green-500 text-white' :
                                stockStatus.status === 'low' ? 'bg-yellow-500 text-white' :
                                'bg-red-500 text-white'
                              }`}>
                                {stockStatus.label}
                              </span>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              <div className="font-bold">{tile.totalFreshBoxes || 0} Boxes</div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tile.totalFreshPieces || 0} Pieces</div>
                              {tile.inTransitBoxes > 0 && (
                                <div className={`text-xs font-semibold mt-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                  🚚 In-Transit: {tile.inTransitBoxes}
                                </div>
                              )}
                              {tile.boxesOrdered > 0 && (
                                <div className={`text-xs font-semibold mt-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                  📋 Ordered: {tile.boxesOrdered}
                                </div>
                              )}
                              {(tile.salesOrder?.freshBoxes > 0 || tile.salesOrder?.freshPieces > 0 || tile.salesOrder?.cornerDamagedBoxes > 0) && (
                                <div className={`text-xs font-semibold mt-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                  🛒 SO: {(tile.salesOrder?.freshBoxes || 0) + (tile.salesOrder?.cornerDamagedBoxes || 0)} B{tile.salesOrder?.freshPieces > 0 ? ` / ${tile.salesOrder.freshPieces} P` : ''}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${
                                tile.status === 'active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                              }`}>
                                {tile.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => openDetailsModal(tile)}
                                  className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-md hover:shadow-lg"
                                  title="View Details"
                                >
                                  <FaEye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleGenerateQR(tile)}
                                  className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-all shadow-md hover:shadow-lg"
                                  title="Generate QR Code"
                                >
                                  <FaQrcode className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEditImage(tile)}
                                  className={`p-2 rounded-lg transition-all shadow-md hover:shadow-lg ${
                                    isDarkMode 
                                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                                  }`}
                                  title="Edit Image"
                                >
                                  <FaImage className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination */}
            {renderPagination()}
          </>
        )}

        {/* No Results */}
        {!loading && !error && tiles.length === 0 && (
          <div className={`rounded-lg shadow-sm border p-12 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="text-center">
              <FaBox className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>No tiles found</h3>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {searchQuery || Object.values(filters).some(f => f) 
                  ? 'Try adjusting your search or filters' 
                  : 'No tiles have been added yet'}
              </p>
              {(searchQuery || Object.values(filters).some(f => f)) && (
                <button
                  onClick={resetFilters}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showDetailsModal && selectedTile && (
        <TileDetailsModal
          tile={selectedTile}
          onClose={() => setShowDetailsModal(false)}
          onEditStock={handleEditStock}
          onEditImage={handleEditImage}
          onStatusChange={handleStatusChange}
          statusChangeLoading={statusChangeLoading}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Stock Edit Modal */}
      {showStockEditModal && selectedStockEdit && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl max-w-md w-full shadow-2xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Edit Stock</h3>
                <button
                  onClick={() => setShowStockEditModal(false)}
                  className={`p-2 rounded-xl transition-colors ${
                    isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Fresh Stock (Boxes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editingStock.freshStockBox}
                    onChange={(e) => setEditingStock(prev => ({ ...prev, freshStockBox: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } border`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Fresh Stock (Pieces)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editingStock.freshStockPieces}
                    onChange={(e) => setEditingStock(prev => ({ ...prev, freshStockPieces: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } border`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Damaged Stock (Boxes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editingStock.damagedStockBox}
                    onChange={(e) => setEditingStock(prev => ({ ...prev, damagedStockBox: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } border`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Damaged Stock (Pieces)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editingStock.damagedStockPieces}
                    onChange={(e) => setEditingStock(prev => ({ ...prev, damagedStockPieces: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } border`}
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowStockEditModal(false)}
                  className={`flex-1 px-4 py-2 rounded-xl font-medium transition-colors ${
                    isDarkMode
                      ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStock}
                  disabled={stockEditLoading}
                  className={`flex-1 px-4 py-2 rounded-xl font-medium transition-colors ${
                    stockEditLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white`}
                >
                  {stockEditLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Edit Modal */}
      {showImageEditModal && selectedImageEdit && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl max-w-md w-full shadow-2xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Update Image</h3>
                <button
                  onClick={() => setShowImageEditModal(false)}
                  className={`p-2 rounded-xl transition-colors ${
                    isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Select New Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className={`w-full px-3 py-2 rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:text-white file:border-0 file:px-4 file:py-2 file:rounded-lg file:mr-4 file:hover:bg-gray-500' 
                        : 'bg-white border-gray-300 text-gray-900 file:bg-gray-100 file:text-gray-700 file:border-0 file:px-4 file:py-2 file:rounded-lg file:mr-4 file:hover:bg-gray-200'
                    } border`}
                  />
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Max size: 5MB. Formats: JPG, JPEG, PNG
                  </p>
                </div>

                {selectedImage && (
                  <div className="mt-4">
                    <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Preview:</p>
                    <img
                      src={URL.createObjectURL(selectedImage)}
                      alt="Preview"
                      className={`w-full h-32 object-cover rounded-xl ${
                        isDarkMode ? 'border-gray-600' : 'border-gray-300'
                      } border`}
                    />
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowImageEditModal(false)}
                  className={`flex-1 px-4 py-2 rounded-xl font-medium transition-colors ${
                    isDarkMode
                      ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveImage}
                  disabled={imageEditLoading || !selectedImage}
                  className={`flex-1 px-4 py-2 rounded-xl font-medium transition-colors ${
                    imageEditLoading || !selectedImage
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white`}
                >
                  {imageEditLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedTileForQR && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-2xl max-w-2xl w-full max-h-[90vh] my-4 shadow-2xl flex flex-col ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0 ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                QR Codes for {selectedTileForQR.tilesName}
              </h3>
              <button
                onClick={() => setShowQRModal(false)}
                className={`p-2 rounded-xl transition-colors flex-shrink-0 ${
                  isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FaTimes className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Hidden SVG QR codes for PDF generation */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
              <QRCodeSVG id="qr-code-svg-objid" value={selectedTileForQR._id} size={200} bgColor="#ffffff" fgColor="#000000" />
              {selectedTileForQR.partNumber && (
                <QRCodeSVG id="qr-code-svg-partno" value={selectedTileForQR.partNumber} size={200} bgColor="#ffffff" fgColor="#000000" />
              )}
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 flex-1">
              {/* ObjectId QR Code */}
              <div className={`p-4 sm:p-6 rounded-xl border ${
                isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <h4 className={`text-sm sm:text-md font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Object ID QR Code
                </h4>
                <div className="flex flex-col items-center space-y-3">
                  <div className="bg-white p-3 sm:p-4 rounded-lg">
                    <img src={qrCodeUrls.objectId} alt="ObjectId QR Code" className="w-48 h-48 sm:w-64 sm:h-64" />
                  </div>
                  <p className={`text-xs sm:text-sm text-center break-all px-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    ID: {selectedTileForQR._id}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <button
                      onClick={() => handleDownloadQR('objectId')}
                      className="px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaDownload className="w-4 h-4" /> 
                      <span>PNG</span>
                    </button>
                    <button
                      onClick={() => { setActiveQRType('objectId'); setTimeout(() => downloadQRPdf(false), 100); }}
                      className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaFilePdf className="w-4 h-4" />
                      <span>PDF</span>
                    </button>
                    <button
                      onClick={() => { setActiveQRType('objectId'); setTimeout(() => downloadQRPdf(true), 100); }}
                      className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaFilePdf className="w-4 h-4" />
                      <span>PDF + Name</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Part Number QR Code */}
              {qrCodeUrls.partNumber && (
                <div className={`p-4 sm:p-6 rounded-xl border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <h4 className={`text-sm sm:text-md font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    Part Number QR Code
                  </h4>
                  <div className="flex flex-col items-center space-y-3">
                    <div className="bg-white p-3 sm:p-4 rounded-lg">
                      <img src={qrCodeUrls.partNumber} alt="Part Number QR Code" className="w-48 h-48 sm:w-64 sm:h-64" />
                    </div>
                    <p className={`text-xs sm:text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Part Number: {selectedTileForQR.partNumber}
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <button
                        onClick={() => handleDownloadQR('partNumber')}
                        className="px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaDownload className="w-4 h-4" /> 
                        <span>PNG</span>
                      </button>
                      <button
                        onClick={() => { setActiveQRType('partNumber'); setTimeout(() => downloadQRPdf(false), 100); }}
                        className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaFilePdf className="w-4 h-4" />
                        <span>PDF</span>
                      </button>
                      <button
                        onClick={() => { setActiveQRType('partNumber'); setTimeout(() => downloadQRPdf(true), 100); }}
                        className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaFilePdf className="w-4 h-4" />
                        <span>PDF + Name</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* QR Name Info */}
              {!qrName && (
                <p className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Using default name "DOMUS" for PDF with name (QR Name not configured for company)
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tile Details Modal Component
const TileDetailsModal = ({ tile, onClose, onEditStock, onEditImage, onStatusChange, statusChangeLoading, isDarkMode }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{tile.tilesName}</h2>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-colors ${
                isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Image and Basic Info */}
            <div>
              <div className="mb-6">
                {tile.imageIds && tile.imageIds.length > 0 ? (
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}/tiles/image/${tile._id}`}
                    alt={tile.tilesName}
                    className={`w-full h-64 object-cover rounded-xl ${
                      isDarkMode ? 'border-gray-600' : 'border-gray-300'
                    } border`}
                  />
                ) : (
                  <div className={`w-full h-64 rounded-xl border flex items-center justify-center ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
                  }`}>
                    <FaImage className={`w-12 h-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                )}
                <button
                  onClick={() => onEditImage(tile)}
                  className={`mt-2 w-full py-2 px-4 rounded-xl font-medium transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                      : 'bg-gray-600 text-white hover:bg-gray-700'
                  }`}
                >
                  <FaImage className="w-4 h-4 inline mr-2" />
                  Update Image
                </button>
              </div>

              <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Basic Information</h3>
                <div className="space-y-2">
                  <DetailRow label="ID" value={tile.id} isDarkMode={isDarkMode} />
                  <DetailRow label="Part Number" value={tile.partNumber} isDarkMode={isDarkMode} />
                  <DetailRow label="Name" value={tile.tilesName} isDarkMode={isDarkMode} />
                  <DetailRow label="Brand" value={tile.brand?.name} isDarkMode={isDarkMode} />
                  <DetailRow label="Category" value={tile.tilesCategory?.parameterName} isDarkMode={isDarkMode} />
                  <DetailRow label="Type" value={tile.tilesType?.parameterName} isDarkMode={isDarkMode} />
                  <DetailRow label="Finish" value={tile.tilesFinishType?.parameterName} isDarkMode={isDarkMode} />
                  <DetailRow label="Size (MM)" value={tile.tilesMM?.parameterName} isDarkMode={isDarkMode} />
                  <DetailRow label="Size (Inches)" value={tile.tilesInches?.parameterName} isDarkMode={isDarkMode} />
                  <DetailRow label="Pieces per Box" value={tile.tilesPiecesPerBox} isDarkMode={isDarkMode} />
                  <DetailRow label="Sqr Ft per Box" value={tile.tilesSqrFtPerBox} isDarkMode={isDarkMode} />
                </div>
              </div>
            </div>

            {/* Right Column - Stock Information */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Stock Information</h3>
                <button
                  onClick={() => onStatusChange(tile)}
                  disabled={statusChangeLoading}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    tile.status === 'active'
                      ? (isDarkMode ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-red-100 text-red-700 hover:bg-red-200')
                      : (isDarkMode ? 'bg-green-900/30 text-green-300 hover:bg-green-900/50' : 'bg-green-100 text-green-700 hover:bg-green-200')
                  }`}
                >
                  {statusChangeLoading ? 'Updating...' : (tile.status === 'active' ? 'Deactivate' : 'Activate')}
                </button>
              </div>

              <div className="space-y-4">
                {tile.stock && tile.stock.length > 0 ? (
                  tile.stock.map((stockEntry, index) => (
                    <div key={index} className={`rounded-xl p-4 ${
                      isDarkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-white border border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {stockEntry.location?.locationName || stockEntry.location?.name || 'Unknown Location'}
                        </h4>
                        <button
                          onClick={() => onEditStock(tile, stockEntry)}
                          className={`text-sm font-medium ${
                            isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                          }`}
                        >
                          Edit Stock
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Fresh Stock:</span>
                          <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                            {stockEntry.freshStockBox || 0} Boxes, {stockEntry.freshStockPieces || 0} Pieces
                          </div>
                        </div>
                        <div>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Damaged Stock:</span>
                          <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                            {stockEntry.damagedStockBox || 0} Boxes, {stockEntry.damagedStockPieces || 0} Pieces
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No stock information available
                  </div>
                )}
              </div>

              {/* Total Stock Summary */}
              <div className={`mt-6 rounded-xl p-4 ${
                isDarkMode ? 'bg-blue-900/30 border border-blue-800/50' : 'bg-blue-50 border border-blue-100'
              }`}>
                <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>Total Stock Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={isDarkMode ? 'text-blue-400' : 'text-blue-700'}>Total Fresh:</span>
                    <div className={`font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
                      {tile.totalFreshBoxes || 0} Boxes, {tile.totalFreshPieces || 0} Pieces
                    </div>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-blue-400' : 'text-blue-700'}>Total Damaged:</span>
                    <div className={`font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
                      {tile.totalDamagedBoxes || 0} Boxes, {tile.totalDamagedPieces || 0} Pieces
                    </div>
                  </div>
                </div>
              </div>

              {/* In-Transit Stock */}
              {tile.inTransitBoxes > 0 && (
                <div className={`mt-4 rounded-xl p-4 ${
                  isDarkMode ? 'bg-orange-900/30 border border-orange-800/50' : 'bg-orange-50 border border-orange-100'
                }`}>
                  <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-orange-300' : 'text-orange-900'}`}>In-Transit Stock</h4>
                  <div className="text-sm">
                    <span className={isDarkMode ? 'text-orange-400' : 'text-orange-700'}>Dispatched:</span>
                    <div className={`font-medium ${isDarkMode ? 'text-orange-200' : 'text-orange-900'}`}>
                      {tile.inTransitBoxes} Boxes
                    </div>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                      These tiles have been dispatched but not yet received.
                    </p>
                  </div>
                </div>
              )}

              {/* Boxes Ordered (Not Yet Dispatched) */}
              {tile.boxesOrdered > 0 && (
                <div className={`mt-4 rounded-xl p-4 ${
                  isDarkMode ? 'bg-amber-900/30 border border-amber-800/50' : 'bg-amber-50 border border-amber-100'
                }`}>
                  <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-900'}`}>Boxes Ordered</h4>
                  <div className="text-sm">
                    <span className={isDarkMode ? 'text-amber-400' : 'text-amber-700'}>Ordered but Not Dispatched:</span>
                    <div className={`font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-900'}`}>
                      {tile.boxesOrdered} Boxes
                    </div>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      These tiles have been ordered but not yet dispatched.
                    </p>
                  </div>
                </div>
              )}

              {/* Sales Ordered (Reserved by Sales Orders) */}
              {(tile.salesOrder?.freshBoxes > 0 || tile.salesOrder?.freshPieces > 0 || tile.salesOrder?.cornerDamagedBoxes > 0 || tile.salesOrder?.cornerDamagedPieces > 0) && (
                <div className={`mt-4 rounded-xl p-4 ${
                  isDarkMode ? 'bg-purple-900/30 border border-purple-800/50' : 'bg-purple-50 border border-purple-100'
                }`}>
                  <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-900'}`}>Sales Ordered</h4>
                  <div className="text-sm">
                    <span className={isDarkMode ? 'text-purple-400' : 'text-purple-700'}>Reserved by Pending Sales Orders:</span>
                    <div className={`font-medium ${isDarkMode ? 'text-purple-200' : 'text-purple-900'}`}>
                      {tile.salesOrder?.freshBoxes > 0 && <span>Fresh: {parseFloat(tile.salesOrder.freshBoxes.toFixed(4))} Boxes{tile.salesOrder?.freshPieces > 0 ? `, ${parseFloat(tile.salesOrder.freshPieces.toFixed(4))} Pcs` : ''}</span>}
                      {tile.salesOrder?.cornerDamagedBoxes > 0 && <span className="ml-3">Corner Dmg: {parseFloat(tile.salesOrder.cornerDamagedBoxes.toFixed(4))} Boxes{tile.salesOrder?.cornerDamagedPieces > 0 ? `, ${parseFloat(tile.salesOrder.cornerDamagedPieces.toFixed(4))} Pcs` : ''}</span>}
                    </div>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                      Quantities reserved by sales orders not yet delivered.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for detail rows
const DetailRow = ({ label, value, className = '', isDarkMode = false }) => (
  <div className={`flex justify-between items-center py-2 border-b last:border-b-0 ${
    isDarkMode ? 'border-gray-600' : 'border-gray-100'
  }`}>
    <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}:</span>
    <span className={`${isDarkMode ? 'text-gray-200' : 'text-gray-900'} ${className}`}>{value || 'N/A'}</span>
  </div>
);

export default TilesStockStatus;
